import { Box, Center, HStack, Skeleton, Table, Tbody, Td, Text, Tooltip, Tr } from '@chakra-ui/react';
import { useFetchClass } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useFormContext } from 'react-hook-form';
import { ReservationFormValues } from 'screens/Class';
import { MindbodyPickASpotClassCell, MindbodyPickASpotClassCellContent } from 'types/class';
import { classHasCapacity } from 'utils/functions';
import * as Sentry from '@sentry/react';

export function ClassRoomLayout({ classId, isEnrollmentInURL }: { classId: string; isEnrollmentInURL: boolean }) {
  const { user } = useUserContext();
  const { setValue, watch } = useFormContext<ReservationFormValues>();
  const selectedSpot = watch('SpotNumber');
  const { data, isLoading } = useFetchClass(classId, isEnrollmentInURL);

  if (isLoading) {
    return (
      <Center>
        <ClassRoomLayoutLoadingPlaceholder />
      </Center>
    );
  }

  // Pour enrollments, on ne veut pas afficher le plan de salle
  if (!data?.PickASpot) {
    return (
      <Text fontWeight="semibold" textAlign="center">
        Pas de plan de salle
      </Text>
    );
  }

  const classIsFull = !classHasCapacity(data);

  const rows = data?.PickASpot.roomLayout.roomLayoutGrid.cells.reduce((rows, cell) => {
    if (!rows[cell.row]) rows[cell.row] = [];
    rows[cell.row].push(cell);
    return rows;
  }, [] as MindbodyPickASpotClassCell[][]);

  const getBackgroundColor = (cell: MindbodyPickASpotClassCell) => {
    const isUserSpot = data?.PickASpot.reservations.some(
      (reservation) =>
        cell.spot?.spotNumber &&
        reservation.spot.spotNumber === cell.spot.spotNumber &&
        reservation.memberExternalId === user?.Id,
    );

    if (isUserSpot) return 'primary.800';

    switch (cell.cellContent) {
      case MindbodyPickASpotClassCellContent.Empty:
        return 'transparent';
      case MindbodyPickASpotClassCellContent.Other:
        return 'blue.200';
      case MindbodyPickASpotClassCellContent.Spot: {
        if (!cell.spot?.spotNumber) return 'transparent';
        const spotReservation = data?.PickASpot.reservations.find(
          (reservation) => cell.spot.spotNumber === reservation.spot.spotNumber,
        );

        if (spotReservation) {
          if (spotReservation.memberExternalId === user?.Id) {
            return 'gray.200';
          }

          return 'gray.300';
        }

        if (classIsFull) return 'gray.300';

        if (selectedSpot === cell.spot.spotNumber) return 'primary.500';

        return 'white';
      }

      case MindbodyPickASpotClassCellContent.Instructor: {
        if (data?.Staff.ImageUrl) {
          return `url(${data?.Staff.ImageUrl}) top/cover no-repeat`;
        } else {
          return 'orange.200';
        }
      }
    }
  };

  const getBorderColor = (cell: MindbodyPickASpotClassCell) => {
    const isUserSpot = data?.PickASpot.reservations.some(
      (reservation) =>
        cell.spot?.spotNumber &&
        reservation.spot.spotNumber === cell.spot.spotNumber &&
        reservation.memberExternalId === user?.Id,
    );

    if (isUserSpot) return 'primary.800';
    switch (cell.cellContent) {
      case MindbodyPickASpotClassCellContent.Empty:
        return 'transparent';
      case MindbodyPickASpotClassCellContent.Other:
        return 'blue.200';
      case MindbodyPickASpotClassCellContent.Spot: {
        if (!cell.spot?.spotNumber) return 'transparent';
        const spotReservation = data?.PickASpot.reservations.find(
          (reservation) => cell.spot.spotNumber === reservation.spot.spotNumber,
        );

        if (spotReservation) {
          if (spotReservation.memberExternalId === user?.Id) {
            return 'gray.500';
          }

          return 'gray.300';
        }

        if (classIsFull) return 'gray.300';

        if (selectedSpot === cell.spot.spotNumber) return 'primary.500';

        return 'black';
      }

      case MindbodyPickASpotClassCellContent.Instructor:
        return 'primary.200';
    }
  };

  const getColor = (cell: MindbodyPickASpotClassCell) => {
    const isUserSpot = data?.PickASpot.reservations.some(
      (reservation) =>
        cell.spot?.spotNumber &&
        reservation.spot.spotNumber === cell.spot.spotNumber &&
        reservation.memberExternalId === user?.Id,
    );

    if (isUserSpot) return 'white';

    if (cell.cellContent === MindbodyPickASpotClassCellContent.Spot) {
      const spotReservation = data?.PickASpot.reservations.find(
        (reservation) => cell.spot.spotNumber === reservation.spot.spotNumber,
      );

      if (spotReservation) {
        if (spotReservation.memberExternalId === user?.Id) {
          return 'gray.500';
        }
        return 'black';
      }

      if (selectedSpot === cell.spot.spotNumber) return 'white';
    }

    return 'black';
  };

  const getCellTooltip = (cell: MindbodyPickASpotClassCell) => {
    if (!cell.spot && cell.cellContent === MindbodyPickASpotClassCellContent.Spot) {
      Sentry.captureMessage('Cell with Spot content but no spot data', {
        level: 'warning',
        tags: {
          where: 'ClassRoomLayout',
        },
        extra: {
          cell,
          classId,
          resourceId: data?.Resource.Id,
        },
      });
    }

    // S'assurer de retourner une valeur par défaut pour les cas non spécifiés
    const defaultTooltip = cell.spot?.spotNumber?.toString() || '';

    // Pour les cellules vides ou "autre"
    if (cell.cellContent === MindbodyPickASpotClassCellContent.Empty) return null;
    if (cell.cellContent === MindbodyPickASpotClassCellContent.Other) return 'Autre';

    // Pour les spots, en particulier avec le siteId 5736551
    if (cell.cellContent === MindbodyPickASpotClassCellContent.Spot) {
      if (mondayMindbody.siteId === '5736551') {
        switch (cell.iconId) {
          case '60d4e39c57443863df72298b': // ID pour "Banc"
            return 'Banc';
          case '60d4e39c57443863df722985': // ID pour "Sac"
            return 'Sac';
          default:
            return defaultTooltip; // Retourne le numéro du spot par défaut
        }
      } else {
        return defaultTooltip; // Pour les autres siteIds, retourne le numéro du spot
      }
    }

    // Pour l'instructeur
    if (cell.cellContent === MindbodyPickASpotClassCellContent.Instructor) {
      return data?.Staff.FirstName || ''; // Retourne le prénom de l'instructeur
    }

    return defaultTooltip; // Retourne une valeur par défaut pour couvrir tous les cas
  };

  return (
    <>
      <Box
        overflowX={{ base: 'auto', md: 'visible' }}
        maxW="100%"
        className={`classRoomLayout classRoomLayout-${data?.Resource.Id}`}
      >
        <Table layout="fixed" maxW={500} m="auto">
          <Tbody>
            {rows?.map((row, index) => (
              <Tr key={index}>
                {row.map((cell) => {
                  const id = cell.column.toString() + cell.row.toString();
                  const isSpot = cell.cellContent === MindbodyPickASpotClassCellContent.Spot;

                  const spotReservation =
                    isSpot &&
                    data.PickASpot.reservations.find(
                      (reservation) => cell.spot.spotNumber === reservation.spot.spotNumber,
                    );

                  const isSpotAvailable = !spotReservation;
                  const background = getBackgroundColor(cell);
                  const borderColor = getBorderColor(cell);
                  const color = getColor(cell);
                  const tooltipLabel = getCellTooltip(cell); // Récupère le label pour le tooltip

                  return (
                    <Td
                      className={`spotCell ${cell.cellContent} ${
                        cell.iconId === '60d4e39c57443863df72298b'
                          ? 'typeA'
                          : cell.iconId === '60d4e39c57443863df722985'
                          ? 'typeB'
                          : `typeOther_${mondayMindbody.siteId}`
                      } ${MindbodyPickASpotClassCellContent.Instructor === cell.cellContent ? 'instructor' : ''}`}
                      aspectRatio="1 / 1"
                      key={id}
                      textAlign="center"
                      borderColor="transparent"
                      pointerEvents={classIsFull ? 'none' : 'all'}
                      p={0}
                      _after={{
                        content: '""',
                        display: 'block',
                        mt: '100%',
                      }}
                      position="relative"
                      onClick={() => {
                        if (isSpotAvailable && cell.spot?.spotNumber) {
                          setValue('SpotNumber', cell.spot.spotNumber);
                        }
                      }}
                    >
                      <Tooltip hasArrow label={tooltipLabel} isDisabled={!tooltipLabel}>
                        <Center
                          cursor={isSpotAvailable ? 'pointer' : 'initial'}
                          position="absolute"
                          borderRadius="50%"
                          borderColor={borderColor}
                          borderWidth={1}
                          bg={background}
                          color={color}
                          top={0}
                          left={0}
                          h="full"
                          w="full"
                          fontSize="sm"
                        >
                          <Text fontWeight="bold">{cell.spot?.spotNumber}</Text>
                        </Center>
                      </Tooltip>
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Center my="4" mt={8}>
        <HStack spacing="20px" w="full" align="center" justifyContent="center">
          <Box display={'flex'} alignItems={'center'}>
            <Box marginRight="5px" w="20px" h="20px" borderRadius="50%" bg="white" border="1px solid"></Box>
            <Text mb="1">Libre</Text>
          </Box>
          <Box display={'flex'} alignItems={'center'}>
            <Box marginRight="5px" w="20px" h="20px" borderRadius="50%" bg="gray.200"></Box>
            <Text mb="1">Réservé</Text>
          </Box>
          <Box display={'flex'} alignItems={'center'}>
            <Box marginRight="5px" w="20px" h="20px" borderRadius="50%" bg={'primary.200'}></Box>
            <Text mb="1">Votre sélection</Text>
          </Box>
          <Box display={'flex'} alignItems={'center'}>
            <Box marginRight="5px" w="20px" h="20px" borderRadius="50%" bg={'primary.800'}></Box>
            <Text mb="1">Votre réservation</Text>
          </Box>
        </HStack>
      </Center>
    </>
  );
}

export function ClassRoomLayoutLoadingPlaceholder() {
  return (
    <Box maxWidth={500} w="full">
      <Skeleton maxWidth={500} w="full" pb={'68%'} />
    </Box>
  );
}
